import React, { useRef } from "react";
import PropTypes from "prop-types";
import { DatePicker } from "rsuite";
import { toast } from "react-toastify";
import { dateFormatter } from "../../../utils/dateUtils";
import { PAST_START_DATE_MESSAGE } from "../constants";
import { startOfDay, isPastDate } from "../utils/dateFieldUtils";

/**
 * SplitDateRangeFields - Start Date and End Date as two separate pickers.
 *
 * Replaces the DateRangePicker when editing an existing promo (HTT-9780): a range
 * picker applies one `shouldDisableDate` rule to both ends, so a promo whose start
 * date has passed cannot have its end date extended. Split apart, each end carries
 * its own rule — the start locks once it is in the past, the end stays extendable.
 *
 * `onChange` keeps the DateRangePicker contract, emitting a `[start, end]` pair, so
 * the existing handleBookingChange / handleStayChange handlers are unaffected.
 *
 * `minDate` is optional and off by default, so the create-promo callers are unchanged.
 * It exists for the promo-expiry review modal, where stay dates may not begin before
 * the booking window — a rule that surface already enforces on save, and which would
 * otherwise be pickable here and only rejected afterwards.
 *
 * `inlinePopup` decides where the calendar overlay is mounted and defaults to the create-promo
 * behaviour. Turn it off wherever these fields sit inside a scrolling, height-capped box — see the
 * note on it below.
 */
function SplitDateRangeFields({
  startDate = null,
  endDate = null,
  onChange,
  hasError = false,
  startPlaceholder = "Start date",
  endPlaceholder = "End date",
  minDate = null,
  inlinePopup = true,
  // Multi-range callers (stay dates) repeat this component per row and only want
  // the "Start Date"/"End Date" captions on the first row. Defaults to true so
  // single-instance callers (booking window) are unchanged.
  showLabels = true,
}) {
  const startPopupRef = useRef(null);
  const endPopupRef = useRef(null);

  // ⚠ Where the calendar overlay is MOUNTED, which is a layout decision, not a cosmetic one.
  //
  // Inline (the default) parks the overlay in a sibling div beside its input. That is what the
  // create-promo page needs: rsuite re-positions an open overlay only when its CONTAINER scrolls
  // (Overlay/Position.js binds one scroll listener, to the container), and on that page the scrolling
  // element is the window — so an overlay anchored anywhere else drifts away from its field.
  //
  // Inside a modal the same choice is wrong. `.rs-modal-body` is `overflow: auto` with a max-height
  // rsuite computes from the viewport, so an overlay mounted inside it is CLIPPED at the body's edge
  // and its full height is added to the body's scroll area — the calendar is cut off, and the modal
  // grows a long empty scroll region beneath the table.
  //
  // Passing no container hands the decision back to rsuite, which is set up for exactly this: an open
  // Modal publishes its wrapper element as the default overlay container through OverlayContext, so
  // the calendar is mounted a level above the dialog — clear of the scrolling body — and rsuite's own
  // `.rs-modal-open .rs-picker-popup { z-index: 1055 }` lifts it over the 1050 dialog.
  const startPopupContainer = inlinePopup
    ? () => startPopupRef.current
    : undefined;
  const endPopupContainer = inlinePopup ? () => endPopupRef.current : undefined;

  // rsuite resolves `autoVertical` against the CONTAINER, so it is only meaningful once the container
  // is a real box — the modal wrapper, which spans the viewport. It opens on whichever side has more
  // room, so a field near the bottom of a tall modal drops its calendar upward instead of off the
  // screen. The inline anchor is a zero-height div, so the same setting there would be measuring
  // nothing; that path stays on "bottom", which is what create-promo ships today.
  const popupPlacement = inlinePopup ? "bottom" : "autoVertical";

  const startLocked = isPastDate(startDate);

  const handleStartChange = (value) => onChange([value ?? null, endDate]);
  const handleEndChange = (value) => onChange([startDate, value ?? null]);

  const notifyStartLocked = () => toast.error(PAST_START_DATE_MESSAGE);

  // An editable start date must not be moved into the past — the range picker
  // enforced this via beforeToday(). Without it a future start could be dragged
  // backwards, which would then read as "already started" and silently switch off
  // the past-start validations that exist for locked dates only. It must also stay
  // on or before the end date; a range picker could not express an inverted pair,
  // and nothing downstream rejects one.
  const shouldDisableStartDate = (date) => {
    if (!date) return false;
    if (isPastDate(date)) return true;
    if (minDate && startOfDay(date) < startOfDay(minDate)) return true;
    return !!endDate && startOfDay(date) > startOfDay(endDate);
  };

  // Never let the end date fall into the past, or behind the start date the promo
  // is already running from — the range picker enforced the latter implicitly.
  const shouldDisableEndDate = (date) => {
    if (!date) return false;
    const day = startOfDay(date);
    if (day < startOfDay(new Date())) return true;
    if (minDate && day < startOfDay(minDate)) return true;
    return !!startDate && day < startOfDay(startDate);
  };

  // rsuite calls renderValue(value, format); dateFormatter's second parameter is
  // includeTime, so passing it directly would render a spurious timestamp.
  const renderDateValue = (value) => dateFormatter(value);

  const errorClass = hasError ? "input-error" : "";

  return (
    <div className="split-date-fields">
      <div className="split-date-field">
        {showLabels && (
          <span className="split-date-field-label">Start Date</span>
        )}
        {startLocked ? (
          // A disabled input emits no click, so something has to catch it and explain why
          // the field will not open. That is a real <button> laid over the picker rather
          // than a div with role="button": it gets Enter/Space activation and focus for
          // free, and it is a SIBLING of the picker, not a wrapper — a <button> may not
          // contain an <input>, so wrapping would be invalid HTML.
          <div className="split-date-locked" title={PAST_START_DATE_MESSAGE}>
            <div className="split-date-locked-inner">
              <DatePicker
                value={startDate}
                disabled
                block
                cleanable={false}
                oneTap
                renderValue={renderDateValue}
                placeholder={startPlaceholder}
                data-testid="start-date-picker"
              />
            </div>
            <button
              type="button"
              className="split-date-locked-overlay"
              aria-label={PAST_START_DATE_MESSAGE}
              onClick={notifyStartLocked}
            />
          </div>
        ) : (
          <div className="condition-input-wrapper">
            <DatePicker
              placement={popupPlacement}
              value={startDate}
              onChange={handleStartChange}
              placeholder={startPlaceholder}
              block
              oneTap
              cleanable={false}
              container={startPopupContainer}
              renderValue={renderDateValue}
              shouldDisableDate={shouldDisableStartDate}
              className={errorClass}
              data-testid="start-date-picker"
            />
            {inlinePopup && (
              <div className="position-relative" ref={startPopupRef} />
            )}
          </div>
        )}
      </div>

      <div className="split-date-field">
        {showLabels && <span className="split-date-field-label">End Date</span>}
        <div className="condition-input-wrapper">
          <DatePicker
            placement={popupPlacement}
            value={endDate}
            onChange={handleEndChange}
            placeholder={endPlaceholder}
            block
            oneTap
            cleanable={false}
            container={endPopupContainer}
            renderValue={renderDateValue}
            shouldDisableDate={shouldDisableEndDate}
            className={errorClass}
            data-testid="end-date-picker"
          />
          {inlinePopup && (
            <div className="position-relative" ref={endPopupRef} />
          )}
        </div>
      </div>
    </div>
  );
}

SplitDateRangeFields.propTypes = {
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  onChange: PropTypes.func.isRequired,
  hasError: PropTypes.bool,
  startPlaceholder: PropTypes.string,
  endPlaceholder: PropTypes.string,
  minDate: PropTypes.instanceOf(Date),
  // False mounts the calendar in rsuite's own default container instead of a sibling div. Set it on
  // any surface whose pickers sit inside a scrolling, height-capped box — see the note above.
  inlinePopup: PropTypes.bool,
  // False hides the "Start Date"/"End Date" captions (repeated multi-range rows).
  showLabels: PropTypes.bool,
};

export default SplitDateRangeFields;
